if (typeof ProductRelated !== 'function') {
  class ProductRelated extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onInput);
      this.rows.forEach((row) => {
        this.renderRow(row);
        row.dataset.effectiveQuantity = String(this.effectiveQuantity(row));
      });
      this.enhanceVariantSelects();
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onInput);
      window.removeEventListener('pageshow', this.onPageShow);
      this.variantDropdownClosers?.forEach((close) => close());
    }

    // Browsers restore form control state on back navigation without firing
    // events, leaving checked boxes with unmuted-but-stale derived state.
    // Reconcile every row (and the combobox triggers) with what the controls
    // actually say; syncRowAndNotify dispatches the effective changes, which
    // brings the sticky price along.
    onPageShow() {
      this.rows.forEach((row) => {
        const select = row.querySelector('[data-js-related-variant]');
        if (select) this.setVariant(row, select);
        else this.syncRowAndNotify(row);
      });
      this.variantSyncers?.forEach((sync) => sync());
    }

    // Replaces each native variant select with an on-page combobox + listbox
    // (APG select-only combobox pattern). The hidden select stays the source
    // of truth: choosing an option writes select.value and dispatches change,
    // so every existing state flow is untouched, and no-JS keeps the native
    // control. Unlike the OS-drawn select popup, this list is page-rendered
    // DOM, so it anchors under its trigger in every environment.
    enhanceVariantSelects() {
      this.variantDropdownClosers = this.variantDropdownClosers || [];
      this.variantSyncers = this.variantSyncers || [];
      this.querySelectorAll('[data-js-related-variant]').forEach((select) => {
        const wrapper = select.closest('.product-related__variant');
        if (!wrapper || select.dataset.enhanced === 'true') return;
        select.dataset.enhanced = 'true';
        wrapper.classList.add('product-related__variant--enhanced');

        const labelText = (this.querySelector(`label[for="${select.id}"]`)?.textContent || '').trim();
        const soldOutLabel = select.dataset.soldOutLabel || '';

        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'product-related__variant-trigger';
        trigger.setAttribute('role', 'combobox');
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        if (labelText) trigger.setAttribute('aria-label', labelText);

        const triggerLabel = document.createElement('span');
        triggerLabel.className = 'product-related__variant-trigger-label';
        const caret = document.createElement('span');
        caret.className = 'product-related__variant-caret';
        caret.setAttribute('aria-hidden', 'true');
        trigger.append(triggerLabel, caret);

        const listbox = document.createElement('ul');
        listbox.className = 'product-related__variant-listbox';
        listbox.setAttribute('role', 'listbox');
        listbox.id = `${select.id}-listbox`;
        if (labelText) listbox.setAttribute('aria-label', labelText);
        listbox.hidden = true;
        trigger.setAttribute('aria-controls', listbox.id);

        Array.from(select.options).forEach((option, index) => {
          const item = document.createElement('li');
          item.className = 'product-related__variant-option';
          item.setAttribute('role', 'option');
          item.id = `${select.id}-option-${index}`;
          item.dataset.index = String(index);
          if (option.disabled) item.setAttribute('aria-disabled', 'true');
          const name = document.createElement('span');
          name.className = 'product-related__variant-option-name';
          name.textContent = option.textContent.trim();
          item.appendChild(name);
          const note = document.createElement('span');
          note.className = 'product-related__variant-option-price';
          note.textContent = option.disabled && soldOutLabel ? soldOutLabel : (option.dataset.price || '');
          if (note.textContent) item.appendChild(note);
          listbox.appendChild(item);
        });

        select.hidden = true;
        wrapper.append(trigger, listbox);

        const state = { open: false, active: -1 };
        const items = () => Array.from(listbox.querySelectorAll('[role="option"]'));
        const enabledIndexes = () => Array.from(select.options).flatMap((option, index) => (option.disabled ? [] : [index]));

        const sync = () => {
          const selected = select.selectedIndex;
          triggerLabel.textContent = select.options[selected]?.textContent.trim() || '';
          items().forEach((item, index) => item.setAttribute('aria-selected', index === selected ? 'true' : 'false'));
        };

        const setActive = (index) => {
          if (index === undefined || index < 0) return;
          state.active = index;
          items().forEach((item, i) => item.classList.toggle('is-active', i === index));
          const item = listbox.children[index];
          if (item) {
            trigger.setAttribute('aria-activedescendant', item.id);
            item.scrollIntoView?.({ block: 'nearest' });
          }
        };

        const onOutsidePointer = (event) => {
          if (!wrapper.contains(event.target)) close();
        };
        const open = () => {
          if (state.open || select.options.length === 0) return;
          state.open = true;
          listbox.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          setActive(select.selectedIndex);
          document.addEventListener('pointerdown', onOutsidePointer, true);
        };
        const close = () => {
          if (!state.open) return;
          state.open = false;
          listbox.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
          trigger.removeAttribute('aria-activedescendant');
          document.removeEventListener('pointerdown', onOutsidePointer, true);
        };
        this.variantDropdownClosers.push(close);

        const choose = (index) => {
          const option = select.options[index];
          if (!option || option.disabled) return;
          if (select.selectedIndex !== index) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          close();
          trigger.focus();
        };

        const step = (delta) => {
          const enabled = enabledIndexes();
          if (!enabled.length) return;
          const position = enabled.indexOf(state.active);
          const start = position === -1 ? (delta > 0 ? -1 : enabled.length) : position;
          const next = Math.min(enabled.length - 1, Math.max(0, start + delta));
          setActive(enabled[next]);
        };

        trigger.addEventListener('click', () => (state.open ? close() : open()));
        trigger.addEventListener('keydown', (event) => {
          const { key } = event;
          if (!state.open) {
            if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
              event.preventDefault();
              open();
            }
            return;
          }
          if (key === 'ArrowDown') { event.preventDefault(); step(1); }
          else if (key === 'ArrowUp') { event.preventDefault(); step(-1); }
          else if (key === 'Home') { event.preventDefault(); setActive(enabledIndexes()[0]); }
          else if (key === 'End') { event.preventDefault(); setActive(enabledIndexes().at(-1)); }
          else if (key === 'Enter' || key === ' ') { event.preventDefault(); choose(state.active); }
          else if (key === 'Escape') { event.preventDefault(); close(); trigger.focus(); }
          else if (key === 'Tab') { close(); }
        });
        listbox.addEventListener('click', (event) => {
          const item = event.target.closest('[role="option"]');
          if (item && item.getAttribute('aria-disabled') !== 'true') choose(Number(item.dataset.index));
        });
        listbox.addEventListener('pointermove', (event) => {
          const item = event.target.closest('[role="option"]');
          if (item && item.getAttribute('aria-disabled') !== 'true' && Number(item.dataset.index) !== state.active) {
            setActive(Number(item.dataset.index));
          }
        });
        select.addEventListener('change', sync);
        this.variantSyncers.push(sync);
        sync();
      });
    }

    get rows() {
      return Array.from(this.querySelectorAll('[data-js-related-row]'));
    }

    onClick(event) {
      const toggle = event.target.closest('[data-js-related-toggle]');
      if (toggle) {
        event.preventDefault();
        this.toggleOverflowRows(toggle);
        return;
      }

      const button = event.target.closest('[data-js-related-increment], [data-js-related-decrement]');
      if (!button || button.disabled) return;

      const row = button.closest('[data-js-related-row]');
      const input = row?.querySelector('[data-js-related-quantity]');
      if (!input) return;

      event.preventDefault();
      const previousQuantity = this.normalizeQuantity(input.value, input);
      const delta = button.hasAttribute('data-js-related-increment') ? 1 : -1;

      // Minus on an included row already at the minimum deselects it instead
      // of stepping below; the kept quantity restores on the next check.
      if (delta === -1 && previousQuantity <= Number(input.min || 0)) {
        const include = row.querySelector('[data-js-related-include]');
        if (include && include.checked && !include.disabled) {
          include.checked = false;
          this.syncRowAndNotify(row);
        }
        return;
      }

      // The mirror gesture: plus on an excluded row selects it at its kept
      // quantity — the first press means "add", not "add and step".
      if (delta === 1 && !this.isIncluded(row)) {
        const include = row.querySelector('[data-js-related-include]');
        if (include && !include.disabled) {
          this.includeRow(row);
          this.syncRowAndNotify(row);
          return;
        }
      }

      const nextQuantity = this.normalizeQuantity(previousQuantity + delta, input);
      this.includeRow(row);
      this.setQuantity(row, nextQuantity);
    }

    onInput(event) {
      const variantSelect = event.target.closest('[data-js-related-variant]');
      if (variantSelect) {
        this.setVariant(variantSelect.closest('[data-js-related-row]'), variantSelect);
        return;
      }

      const include = event.target.closest('[data-js-related-include]');
      if (include) {
        // The quantity keeps its value on uncheck so re-adding restores the
        // same amount; only the effective (included) quantity changes.
        const row = include.closest('[data-js-related-row]');
        const quantityInput = row?.querySelector('[data-js-related-quantity]');
        if (!quantityInput || quantityInput.disabled) return;
        this.syncRowAndNotify(row);
        return;
      }

      const input = event.target.closest('[data-js-related-quantity]');
      if (!input) return;
      const row = input.closest('[data-js-related-row]');
      this.includeRow(row);
      this.setQuantity(row, this.normalizeQuantity(input.value, input));
    }

    // Expand/collapse the rows past the visible count. CSS owns the hiding (and
    // keeps any included row visible regardless), so this only flips the state
    // flag and relabels the control.
    toggleOverflowRows(toggle) {
      const expanded = this.dataset.expanded === 'true';
      const next = !expanded;
      this.dataset.expanded = String(next);
      toggle.setAttribute('aria-expanded', String(next));

      // CSS keeps an included overflow row visible even while collapsed, so
      // counting every overflow row would over-report what is actually hidden.
      const hidden = Array.from(this.querySelectorAll('.product-related__item--overflow'))
        .filter((row) => !this.isIncluded(row)).length;
      const label = next ? toggle.dataset.labelLess : toggle.dataset.labelMore;
      // Write to the label span so the caret beside it survives; fall back to
      // the button itself for markup that has no span.
      const target = toggle.querySelector('[data-js-related-toggle-label]') || toggle;
      target.textContent = next || !hidden ? label : `${label} (${hidden})`;
    }

    isIncluded(row) {
      const include = row?.querySelector('[data-js-related-include]');
      if (!include) return true;
      return include.checked && !include.disabled;
    }

    effectiveQuantity(row) {
      const input = row?.querySelector('[data-js-related-quantity]');
      if (!input || input.disabled || !this.isIncluded(row)) return 0;
      return this.normalizeQuantity(input.value, input);
    }

    includeRow(row) {
      const include = row?.querySelector('[data-js-related-include]');
      if (include && !include.disabled) include.checked = true;
    }

    normalizeQuantity(value, input) {
      const minimum = Number(input?.min || 0);
      const maximum = Number(input?.max || 99);
      const parsed = Number.parseInt(value, 10);
      const quantity = Number.isFinite(parsed) ? parsed : minimum;
      return Math.min(maximum, Math.max(minimum, quantity));
    }

    setQuantity(row, quantity) {
      const input = row?.querySelector('[data-js-related-quantity]');
      if (!input) return;

      input.value = quantity;
      input.dataset.previousQuantity = String(quantity);
      this.syncRowAndNotify(row);
    }

    // The per-row baseline is the only dispatch gate, so duplicate DOM events
    // (browsers fire both input and change for a checkbox click) collapse to
    // one pdp-product-related-change per real state change. The baseline covers
    // the variant too: swapping a checked row to a differently priced variant
    // changes what it contributes even when the quantity holds.
    syncRowAndNotify(row) {
      const previousQuantity = Number(row.dataset.effectiveQuantity || 0);
      const previousVariantId = row.dataset.effectiveVariantId || '';
      this.renderRow(row);

      const quantity = this.effectiveQuantity(row);
      const variantId = quantity > 0
        ? (row.querySelector('[data-js-related-eligible]')?.dataset.variantId || '')
        : '';
      row.dataset.effectiveQuantity = String(quantity);
      row.dataset.effectiveVariantId = variantId;

      if (quantity !== previousQuantity || variantId !== previousVariantId) {
        this.dispatchRowChange(row, previousQuantity, quantity, variantId);
      }
    }

    // Takes the effective variant the gate above already resolved rather than
    // re-reading the DOM: an excluded row contributes no variant, so reporting
    // one would contradict the quantity in the same payload.
    dispatchRowChange(row, previousQuantity, quantity, variantId) {
      const detail = {
        primaryProductId: this.dataset.primaryProductId || null,
        productId: row.dataset.productId || null,
        variantId: variantId || null,
        previousQuantity,
        quantity
      };

      this.dispatchEvent(new CustomEvent('pdp-product-related-change', {
        bubbles: true,
        detail
      }));
      this.publishAnalytics('pdp_related_change', detail);
    }

    // Republishes the existing DOM event through Shopify's Customer Events
    // pipeline (analytics.publish) so a merchant-configured Custom Pixel can
    // forward it without a theme deploy. Per spec §17, analytics must never
    // block the interaction it describes.
    publishAnalytics(name, detail) {
      try {
        window.Shopify?.analytics?.publish(name, detail);
      } catch (_error) {
        // Swallow: a broken or absent pixel must not affect the related-items UI.
      }
    }

    setVariant(row, select) {
      const selectedOption = select?.selectedOptions?.[0];
      const eligible = row?.querySelector('[data-js-related-eligible]');
      const input = row?.querySelector('[data-js-related-quantity]');
      if (!selectedOption || !eligible || !input) return;

      const available = selectedOption.dataset.available === 'true';
      eligible.dataset.variantId = selectedOption.value;
      // Keep the chosen quantity while unavailable so switching back restores it.
      input.disabled = !available;

      const inventoryMax = Number.parseInt(selectedOption.dataset.inventoryMax, 10);
      if (Number.isFinite(inventoryMax) && inventoryMax > 0) input.max = String(inventoryMax);

      const price = row.querySelector('[data-js-related-price]');
      if (price && selectedOption.dataset.price) {
        price.textContent = selectedOption.dataset.price;
      }

      this.syncRowAndNotify(row);
    }

    renderRow(row) {
      const input = row.querySelector('[data-js-related-quantity]');
      if (!input) return;
      const quantity = this.normalizeQuantity(input.value, input);
      input.value = quantity;
      input.dataset.previousQuantity = String(quantity);

      const decrement = row.querySelector('[data-js-related-decrement]');
      const increment = row.querySelector('[data-js-related-increment]');
      const minimum = Number(input.min || 0);
      const maximum = Number(input.max || 99);
      const include = row.querySelector('[data-js-related-include]');
      if (include) {
        include.disabled = input.disabled;
        if (input.disabled) include.checked = false;
      }

      // At the minimum, minus stays live on an included row (it deselects);
      // rows without a checkbox keep the plain lower bound.
      const canDeselect = !!include && include.checked && !include.disabled;
      if (decrement) decrement.disabled = input.disabled || (quantity <= minimum && !canDeselect);
      if (increment) increment.disabled = input.disabled || quantity >= maximum;
    }

    getSelectedItems() {
      return this.rows.reduce((items, row) => {
        const eligible = row.querySelector('[data-js-related-eligible][data-variant-id]');
        if (!eligible) return items;

        const id = Number(eligible.dataset.variantId);
        const quantity = this.effectiveQuantity(row);
        if (!Number.isSafeInteger(id) || id <= 0 || quantity <= 0) return items;

        items.push({
          id,
          quantity,
          productId: row.dataset.productId || null,
          title: row.dataset.productTitle || ''
        });
        return items;
      }, []);
    }
  }

  if (typeof customElements.get('product-related') === 'undefined') {
    customElements.define('product-related', ProductRelated);
  }
}

/* ---
  Complementary recommendations

  This block cannot reuse <product-recommendations>: sections/product-recommendations.liquid
  defines its own class for that tag in a {% javascript %} block and registers it first, so
  the richer implementation in component-product-form.js is skipped by its own
  customElements.get guard. The registered one swaps only when the response contains
  `.product-item` cards, so these [data-js-related-row] rows were fetched and discarded.
  Owning a distinct tag removes the collision instead of depending on script order.

  Loads on intersection, per spec 5.1.3 — the block sits below the purchase controls, so
  fetching on connect would pull a section every PDP load for content most shoppers never
  scroll to.
--- */

if (typeof ComplementaryRecommendations !== 'function') {
  class ComplementaryRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true' || !this.dataset.url) return;

      const load = () => {
        if (this.dataset.loaded === 'true') return;
        this.dataset.loaded = 'true';
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const parsed = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('complementary-recommendations');

            // Shopify returns the section shell even with nothing to recommend;
            // only swap when it actually carries rows, so an empty result leaves
            // the block collapsed rather than replacing it with an empty card.
            if (parsed && parsed.querySelector('[data-js-related-row]')) {
              this.innerHTML = parsed.innerHTML;
              this.dispatchEvent(new Event('complementary-recommendations-loaded'));
            } else {
              this.dispatchEvent(new Event('complementary-recommendations-empty'));
            }
          })
          .catch(() => {
            this.dispatchEvent(new Event('complementary-recommendations-empty'));
          });
      };

      // The element is empty until it loads, so it has no height of its own; the
      // rootMargin is what gives it something to intersect with ahead of time.
      if (window.IntersectionObserver) {
        this.observer = new IntersectionObserver((entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          load();
        }, { rootMargin: '0px 0px 400px 0px' });
        this.observer.observe(this);
      } else {
        load();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  if (typeof customElements.get('complementary-recommendations') === 'undefined') {
    customElements.define('complementary-recommendations', ComplementaryRecommendations);
  }
}
